// Accounting › Overview, "Where the money stands" (Blueprint's; screens/accounting.js), its own module out of
// Books.tsx, on ui-web's DashboardPage (patterns/README.md, BL-189). The title block's figures are cash in
// the bank, what's owed to us, the bills to pay, the tax owing (only the taxes the company charges) and
// this month's profit, each a Skeleton until its answer comes. Then who owes what (Aging) beside what
// needs a person (a RecordList, each row opening its part), and under it the nightly books check: one
// Notice, each broken rule as a row with its documents, and the imported records that keep their source's
// own figures, folded in a row that opens in place. The reads are Overview.tsx's (useBooksOverview);
// Riverstone keeps its tiles there. overview-page.css spaces the parts, tokens only.
import {
  Aging,
  Figures,
  Notice,
  RecordList,
  RecordRow,
  RowList,
  Skeleton,
  type Row,
} from '@flowology/ui-web/fl';
import { DashboardPage, type DashboardSection } from '@flowology/ui-web/patterns';
import { Fragment, useState } from 'react';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { DocLink } from '../shared/DocLink.tsx';
import { integrityView, type IntegrityRow, type IntegrityStatus } from './integrity-model.ts';
import { cashNote, money, monthName, seesPayroll, total } from './model.ts';
import { officeHost } from './office-host.ts';
import { useBooksOverview } from './Overview.tsx';
import { BOOKS_PARTS } from './parts.ts';
import type { BooksPart } from './props.ts';
import { useQuery } from './ui.tsx';
import './overview-page.css';

const PART = BOOKS_PARTS.find((p) => p.part === 'overview')!;

/** "INV-1042, INV-1043 and 2 more", each number a link to its document when it has one. */
function Names({ shown, more }: { shown: IntegrityRow[]; more: number }) {
  return (
    <>
      {shown.map((r, i) => (
        <Fragment key={r.key}>
          {i > 0 && ', '}
          <DocLink doc={r.doc}>{r.label}</DocLink>
        </Fragment>
      ))}
      {more > 0 && ` and ${more} more`}
    </>
  );
}

/** The nightly books check (books.integrityStatus): it only reports; nothing here changes anything. */
function NightlyCheck({
  go,
  timeZone,
  held,
}: {
  go: (p: BooksPart) => void;
  timeZone?: string;
  held: readonly string[];
}) {
  const q = useQuery<IntegrityStatus | null>('books.integrityStatus', {});
  const [importedOpen, setImportedOpen] = useState(false);
  // Before it loads, or for a role that can't read the books, say nothing.
  if (q.data === undefined) return null;
  const v = integrityView(
    q.data,
    timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    new Date(),
    (part) => part !== 'payroll' || seesPayroll(held),
  );
  return (
    <section className="bp-ov-check" aria-label="Nightly books check">
      <Notice tone={v.tone === 'ok' ? 'done' : 'waiting'} title={v.head} />
      {v.problems.length > 0 && (
        <RecordList label="What the check found">
          {v.problems.map((p) => {
            // Its documents link to themselves; a rule without one opens the Books part to look in.
            const linked = p.shown.some((r) => r.doc);
            const sub = (
              <>
                <Names shown={p.shown} more={p.more} />
                {p.go && !linked ? ` · in ${p.go.label}` : ''}
              </>
            );
            return p.go && !linked ? (
              <RecordRow key={p.id} title={p.says} sub={sub} label={p.says} onOpen={() => go(p.go!.part)} />
            ) : (
              <RecordRow key={p.id} title={p.says} sub={sub} />
            );
          })}
        </RecordList>
      )}
      {v.alsoUnfinished && <Notice title={v.alsoUnfinished} />}
      {v.importedCount > 0 && (
        // a row-sized disclosure, as the page's line was: opened, the records by rule
        <RecordList label="Imported records">
          <RecordRow
            id="bp-ov-imported"
            title={
              v.importedCount === 1
                ? '1 imported record keeps its source’s own figures, for the migration’s reconciliation'
                : `${v.importedCount} imported records keep their source’s own figures, for the migration’s reconciliation`
            }
            isOpen={importedOpen}
            onToggle={() => setImportedOpen((o) => !o)}
            expanded={
              <RowList
                label="Imported records by rule"
                rows={v.imported.map((i): Row => ({
                  id: i.title,
                  name: i.title,
                  lines: [{ text: <Names shown={i.shown} more={i.more} /> }],
                }))}
              />
            }
          />
        </RecordList>
      )}
    </section>
  );
}

export function OverviewPage({
  nav,
  go,
  held,
  timeZone,
}: {
  nav: NavProps;
  go: (p: BooksPart) => void;
  held: readonly string[];
  timeZone?: string;
}) {
  const host = officeHost(go);
  const { today, mStart, mEnd, pStart, banks, ar, ap, pl, codes, gst, pst, cash, overdue, owing, todo } =
    useBooksOverview();
  const crumb = usePageCrumb(nav);
  const fig = (cents: number | null) => (cents === null ? <Skeleton width="short" /> : money(cents));
  const taxes = (['GST', 'PST'] as const).filter((c) => codes?.includes(c));
  const taxOwing = taxes.map((c) => owing(c === 'GST' ? gst.data : pst.data));
  const note = banks.data ? cashNote(banks.data) : undefined;
  const figures = [
    { label: 'Cash in bank', value: fig(cash), ...(note ? { sub: note } : {}) },
    {
      label: 'Owed to us',
      value: fig(ar.data?.openInvoicesCents ?? null),
      ...(ar.data ? { sub: overdue ? `${money(overdue)} of it late` : 'None late' } : {}),
    },
    {
      label: 'Bills to pay',
      value: fig(ap.data?.openBillsCents ?? null),
      ...(ap.data
        ? { sub: `${ap.data.rows.length} approved bill${ap.data.rows.length === 1 ? '' : 's'}` }
        : {}),
    },
    ...(taxes.length > 0
      ? [
          {
            label: `${taxes.join(' and ')} owing`,
            value: fig(taxOwing.some((t) => t === null) ? null : total(taxOwing, (t) => t ?? 0)),
            sub: monthName(pStart),
          },
        ]
      : []),
    {
      label: `Profit, ${monthName(mStart)}`,
      value: fig(pl.data?.netProfitCents ?? null),
      ...(pl.data
        ? { sub: `Income ${money(pl.data.incomeCents)} to ${mEnd > today ? 'today' : 'month end'}` }
        : {}),
    },
  ];
  const owed: DashboardSection = ar.data
    ? { title: 'Who owes what', children: <Aging data={ar.data} host={host} /> }
    : ar.error
      ? {
          title: 'Who owes what',
          status: 'failed',
          failedText: ar.error,
          onRetry: () => void ar.reload(),
        }
      : { title: 'Who owes what', status: 'loading', what: 'who owes what' };
  const needs: DashboardSection = {
    title: 'Needs you',
    count: todo.length,
    children: (
      <>
        {todo.length === 0 ? (
          <RowList label="Needs you" rows={[]} empty="Nothing waiting. The books are up to date." />
        ) : (
          <RecordList label="Needs you">
            {todo.map((t) => (
              <RecordRow key={t.what} title={t.what} sub={t.why} label={t.what} onOpen={() => go(t.go)} />
            ))}
          </RecordList>
        )}
        <NightlyCheck go={go} held={held} {...(timeZone ? { timeZone } : {})} />
      </>
    ),
  };
  return (
    <div className="app">
      <main>
        <PageBanners nav={nav} />
        <DashboardPage
          head={{
            ...(crumb ? { crumb } : {}),
            title: PART.bp ?? PART.rail,
            lede: PART.lede,
            figures: <Figures items={figures} />,
          }}
          main={[owed]}
          side={[needs]}
        />
      </main>
    </div>
  );
}
